import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Calendar, 
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Receipt,
  FileText,
  CheckCircle,
  XCircle,
  Shield,
  User as UserIcon,
  UserCheck,
  UserX,
  RefreshCw
} from 'lucide-react';
import SuperAdminSidebar from '../../../components/SuperAdminsidebar';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { fetchUserDetails, activateDeactivateUser, fetchUserFinancialSummary, fetchUserMonthlyComparison, fetchUserRecentActivities } from '../../../../services/superadmin_services/user_management_services';

const UserDetailsPage = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [monthlyComparison, setMonthlyComparison] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Load user details and all statistics in parallel
      const [userResponse, summaryResponse, comparisonResponse, activitiesResponse] = await Promise.all([
        fetchUserDetails(userId),
        fetchUserFinancialSummary(userId),
        fetchUserMonthlyComparison(userId),
        fetchUserRecentActivities(userId)
      ]);
      
      setUser(userResponse.data.user);
      setFinancialSummary(summaryResponse.data);
      setMonthlyComparison(comparisonResponse.data);
      setRecentActivities(activitiesResponse.data.activities || []);
    } catch (error) {
      setError(error.message);
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatusClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await activateDeactivateUser(userId);
      
      // Update the user in the local state
      setUser(prevUser => ({ ...prevUser, ...response.data.user }));
      
      // Show success message (you can add a toast notification here)
      console.log(response.message);
    } catch (error) {
      setError(error.message);
      console.error('Error toggling user status:', error);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleCancelToggleStatus = () => {
    setShowConfirmModal(false);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'expense':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'revenue':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'bill':
        return <Receipt className="w-4 h-4 text-orange-500" />;
      case 'proforma':
        return <FileText className="w-4 h-4 text-blue-500" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusIcon = (status) => {
    return status === 'active' 
      ? <CheckCircle className="w-5 h-5 text-green-500" />
      : <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getUserRole = (user) => {
    if (user.is_superuser) return 'Super Admin';
    if (user.is_staff) return 'Admin';
    return 'User';
  };

  const getRoleBadgeColor = (user) => {
    if (user.is_superuser) return 'bg-secondary-100 text-secondary-800';
    if (user.is_staff) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Format currency in XAF (Cameroon Francs)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const getConfirmationMessage = () => {
    if (!user) return '';
    
    const isActive = user.active_status === 'active' || user.is_active;
    const action = isActive ? 'deactivate' : 'activate';
    const userName = user.first_name && user.last_name 
      ? `${user.first_name} ${user.last_name}` 
      : user.username;
    
    return `Are you sure you want to ${action} ${userName}? ${
      isActive 
        ? 'This will prevent the user from accessing the system.' 
        : 'This will allow the user to access the system again.'
    }`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="text-center py-12">
            <p className="text-gray-500">Loading user details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={loadUserData}
              className="mt-4 bg-secondary-600 text-white px-4 py-2 rounded-lg hover:bg-secondary-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="text-center py-12">
            <p className="text-gray-500">User not found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SuperAdminSidebar />
      
      <div className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/superadmin/users"
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
                <p className="text-gray-600 mt-1">
                  Detailed information and activity for {user.first_name && user.last_name 
                    ? `${user.first_name} ${user.last_name}` 
                    : user.username
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={loadUserData}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
              <button 
                onClick={handleToggleUserStatusClick}
                disabled={loading}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50 ${
                  (user.active_status === 'active' || user.is_active)
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {(user.active_status === 'active' || user.is_active) ? (
                  <>
                    <UserX className="w-4 h-4" />
                    <span>Deactivate User</span>
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4" />
                    <span>Activate User</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserIcon className="w-12 h-12 text-secondary-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {user.first_name && user.last_name 
                    ? `${user.first_name} ${user.last_name}` 
                    : user.username
                  }
                </h2>
                <p className="text-gray-600">@{user.username}</p>
                <div className="flex items-center justify-center space-x-2 mt-2">
                  {getStatusIcon(user.active_status || (user.is_active ? 'active' : 'inactive'))}
                  <span className="text-sm font-medium capitalize">
                    {user.active_status || (user.is_active ? 'active' : 'inactive')}
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getRoleBadgeColor(user)}`}>
                    {user.is_superuser && <Shield className="w-4 h-4 mr-1" />}
                    {getUserRole(user)}
                  </span>
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{user.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {user.updated_at && (
                    <div className="flex items-center space-x-3">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Last updated: {new Date(user.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {user.address && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-900 mb-1">Address</p>
                    <p className="text-sm text-gray-600">{user.address}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content - Financial Data */}
          <div className="lg:col-span-2 space-y-6">
            {loading ? (
              <div className="space-y-6">
                {/* Loading skeleton for financial overview */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="p-4 bg-gray-50 rounded-lg">
                          <div className="h-8 bg-gray-200 rounded mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded mb-1"></div>
                          <div className="h-6 bg-gray-200 rounded mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Loading skeleton for monthly comparison */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="p-4 border border-gray-200 rounded-lg">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-6 bg-gray-200 rounded mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Loading skeleton for recent activities */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="space-y-4">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                          <div className="h-8 w-8 bg-gray-200 rounded"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded mb-1"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          </div>
                          <div className="h-6 w-16 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                <p className="font-medium">Error loading financial data</p>
                <p className="text-sm mt-1">{error}</p>
                <button
                  onClick={loadUserData}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Retry
                </button>
              </div>
            ) : financialSummary ? (
              <>
                {/* Financial Overview */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Overview</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <TrendingDown className="w-8 h-8 text-red-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Expenses</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(financialSummary.expenses?.total_amount || 0)}</p>
                      <p className="text-xs text-gray-500">{financialSummary.expenses?.count || 0} transactions</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Revenues</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(financialSummary.revenues?.total_amount || 0)}</p>
                      <p className="text-xs text-gray-500">{financialSummary.revenues?.count || 0} transactions</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <Receipt className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Bills</p>
                      <p className="text-xl font-bold text-orange-600">{formatCurrency(financialSummary.bills?.total_amount || 0)}</p>
                      <p className="text-xs text-gray-500">{financialSummary.bills?.count || 0} bills</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Proformas</p>
                      <p className="text-xl font-bold text-blue-600">{financialSummary.proformas?.count || 0}</p>
                      <p className="text-xs text-gray-500">proformas created</p>
                    </div>
                  </div>
                  
                  {/* Net Profit Calculation */}
                  <div className="mt-6 p-4 bg-secondary-50 rounded-lg">
                    <div className="text-center">
                      <DollarSign className="w-8 h-8 text-secondary-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Net Profit</p>
                      <p className="text-2xl font-bold text-secondary-600">
                        {formatCurrency(
                          (financialSummary.revenues?.total_amount || 0) - 
                          (financialSummary.expenses?.total_amount || 0) - 
                          (financialSummary.bills?.total_amount || 0)
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        Revenues - Expenses - Bills
                      </p>
                    </div>
                  </div>
                </div>

                {/* Monthly Comparison */}
                {monthlyComparison && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Monthly Comparison: {monthlyComparison.month_names?.current} vs {monthlyComparison.month_names?.last}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Expenses</span>
                          <TrendingDown className="w-4 h-4 text-red-500" />
                        </div>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(monthlyComparison.current_month?.expenses?.total_amount || 0)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {calculatePercentageChange(
                            monthlyComparison.current_month?.expenses?.total_amount || 0,
                            monthlyComparison.last_month?.expenses?.total_amount || 0
                          )}% vs last month
                        </p>
                        <p className="text-xs text-gray-400">
                          {monthlyComparison.current_month?.expenses?.count || 0} this month, {monthlyComparison.last_month?.expenses?.count || 0} last month
                        </p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Revenues</span>
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        </div>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(monthlyComparison.current_month?.revenues?.total_amount || 0)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {calculatePercentageChange(
                            monthlyComparison.current_month?.revenues?.total_amount || 0,
                            monthlyComparison.last_month?.revenues?.total_amount || 0
                          )}% vs last month
                        </p>
                        <p className="text-xs text-gray-400">
                          {monthlyComparison.current_month?.revenues?.count || 0} this month, {monthlyComparison.last_month?.revenues?.count || 0} last month
                        </p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Bills</span>
                          <Receipt className="w-4 h-4 text-orange-500" />
                        </div>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(monthlyComparison.current_month?.bills?.total_amount || 0)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {calculatePercentageChange(
                            monthlyComparison.current_month?.bills?.total_amount || 0,
                            monthlyComparison.last_month?.bills?.total_amount || 0
                          )}% vs last month
                        </p>
                        <p className="text-xs text-gray-400">
                          {monthlyComparison.current_month?.bills?.count || 0} this month, {monthlyComparison.last_month?.bills?.count || 0} last month
                        </p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Proformas</span>
                          <FileText className="w-4 h-4 text-blue-500" />
                        </div>
                        <p className="text-lg font-bold text-gray-900">
                          {monthlyComparison.current_month?.proformas?.count || 0}
                        </p>
                        <p className="text-xs text-gray-500">
                          {calculatePercentageChange(
                            monthlyComparison.current_month?.proformas?.count || 0,
                            monthlyComparison.last_month?.proformas?.count || 0
                          )}% vs last month
                        </p>
                        <p className="text-xs text-gray-400">
                          {monthlyComparison.current_month?.proformas?.count || 0} this month, {monthlyComparison.last_month?.proformas?.count || 0} last month
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Activities */}
                {recentActivities.length > 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
                    <div className="space-y-4">
                      {recentActivities.map((activity) => (
                        <div key={activity.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-shrink-0">
                            {getActivityIcon(activity.activity_type)}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                            <p className="text-xs text-gray-500">{activity.formatted_date}</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              activity.activity_type === 'expense' || activity.activity_type === 'bill' 
                                ? 'bg-red-100 text-red-800' 
                                : activity.activity_type === 'revenue'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {activity.action}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No recent activities found for this user.</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-500">No financial data available for this user.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelToggleStatus}
        onConfirm={handleConfirmToggleStatus}
        title={user ? 
          `${(user.active_status === 'active' || user.is_active) ? 'Deactivate' : 'Activate'} User` 
          : 'Confirm Action'
        }
        message={getConfirmationMessage()}
      />
    </div>
  );
};

export default UserDetailsPage;